import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import {
  clearJobCandidates,
  clearSelectedJob,
  FETCH_JOB_ACTION,
  FETCH_JOB_CANDIDATES_ACTION,
  fetchJob,
  fetchJobCandidates,
  fetchProfessionalProfile,
} from '~/actions';
import {
  BackButton,
  Box,
  CardSpinner,
  Column,
  FloatingContainer,
  Row,
  Screen,
  Text,
  ZStack,
} from '~/commonComponents';
import {
  CandidateStatus,
  CandidateStatusMapper,
  useGetCandidateOnsiteInterviewList,
  useGetCandidateProfessional,
} from '~/domain/PermanentJobs';
import { useAppSelector, useIsSuccess, useLoading } from '~/hooks';
import {
  CheckCircleOutlineIcon,
  CircleCheckSolidIcon,
  CloseCircleOutlineIcon,
} from '~/images/icons';
import { IJobReducerState } from '~/reducers/jobReducer/interfaces';
import { Colors } from '~/themes/colors';
import { HirePopupConfirmation } from '../PermanentJobsV2/JobOverview/components/CandidateTabView/CandidateListTable/HirePopup';
import { RejectPopupConfirmation } from '../PermanentJobsV2/JobOverview/components/CandidateTabView/CandidateListTable/RejectPopup';
import { ProfessionalProfile } from './components';
import { ExperienceOverview, OnsiteInterviewsPanel } from './containers';
import { useHireCandidate } from './hooks/useHireCandidate';
import { useRejectCandidate } from './hooks/useRejectCandidate';
import { TCandidateOverviewPageRoutes } from './Types';

export const CandidateOverview = () => {
  const dispatch = useDispatch();

  const { jobId, professionalId } = useParams<TCandidateOverviewPageRoutes>();

  const { isLoading } = useLoading([FETCH_JOB_ACTION, FETCH_JOB_CANDIDATES_ACTION]);
  const { isSuccess: isSuccessFetchJobs } = useIsSuccess([FETCH_JOB_ACTION]);

  const { job: currentJob, allCandidates: allCounters } = useAppSelector(
    (state) => state.job as IJobReducerState,
  );
  const currentCounter = allCounters.find((it) => it.candidate.id === professionalId);
  const professional = useAppSelector((state) => state.user.professionalProfile);

  const { data: candidate, isInitialLoading: isGetCandidateProfessionalLoading } =
    useGetCandidateProfessional(
      {
        postingId: currentJob?.postingId || '',
        professionalId: professional?.id || '',
      },
      { enabled: !!currentJob?.postingId && !!professional?.id },
    );

  const { data, isInitialLoading: isGetCandidateOnsiteInterviewListLoading } =
    useGetCandidateOnsiteInterviewList(
      {
        postingId: currentJob?.postingId || '',
        professionalId: professional?.id || '',
      },
      {
        enabled: !!currentJob?.postingId && !!professional?.id,
      },
    );

  const {
    canHire,
    controls: controlsHire,
    isOpen: isHireOpen,
    handleHireClick,
  } = useHireCandidate({
    candidate,
    hasCompletedInterview: data?.hasCompletedInterviews,
  });

  const {
    canReject,
    controls: controlsReject,
    isOpen: isRejectOpen,
    handleRejectClick,
  } = useRejectCandidate({ candidate });

  useEffect(() => {
    dispatch(clearSelectedJob());
    dispatch(clearJobCandidates());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch job data and professional profile - required to render candidate overview page
  useEffect(() => {
    if (jobId && professionalId) {
      dispatch(fetchJob({ jobId, candidateId: professionalId, requestCandidateOvertime: false }));
      dispatch(fetchProfessionalProfile(professionalId));
    }
  }, [dispatch, jobId, professionalId]);

  // Fetch job candidates (counters) - provides background check status, DSO/office match data, and company match labels
  useEffect(() => {
    if (isSuccessFetchJobs && !!currentJob) {
      dispatch(fetchJobCandidates({ jobId }));
    }
  }, [currentJob, dispatch, isSuccessFetchJobs, jobId]);

  if (
    isLoading ||
    !currentJob ||
    !professional ||
    isGetCandidateProfessionalLoading ||
    isGetCandidateOnsiteInterviewListLoading
  ) {
    return <CardSpinner />;
  }

  return (
    <>
      <Screen>
        <FloatingContainer borderRadius={0} flex={1} paddingBlock={54} paddingInline={48}>
          <Row position="relative" alignItems="center" justifyContent="center">
            <ZStack left={0}>
              <BackButton />
            </ZStack>
            <Column>
              <Text bold fontSize={32} textAlign="center">
                Candidate Overview
              </Text>
              {data?.officeStatus && (
                <Text
                  alignItems="center"
                  bold
                  display="inline-flex"
                  fontSize={18}
                  gap={4}
                  justifyContent="center"
                >
                  Status:{' '}
                  <Text color={CandidateStatusMapper.getColor(data.officeStatus)} fontSize={18}>
                    {CandidateStatusMapper.toHumanReadable(data.officeStatus).toUpperCase()}
                  </Text>
                  {data.officeStatus === CandidateStatus.Hired && <CircleCheckSolidIcon />}
                </Text>
              )}
            </Column>
          </Row>

          <Row gap={20} marginTop={48}>
            <Box flex={1}>
              <ProfessionalProfile
                disableNavigation
                hasWorkedWithDSO={currentCounter?.hasWorkedWithDSO}
                hasWorkedWithOffice={currentCounter?.hasWorkedWithOffice}
                job={currentJob}
                professional={professional}
              />
            </Box>
            <Row flex={1} alignItems="center" justifyContent="center" gap={16}>
              <button
                className="shadow-xs hover__shadow-sm"
                data-cy="candidate-action-reject"
                disabled={!canReject}
                type="button"
                style={{
                  alignItems: 'center',
                  background: Colors.warning_500,
                  borderRadius: 48,
                  cursor: canReject ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  gap: 6,
                  height: 48,
                  justifyContent: 'center',
                  opacity: canReject ? 1 : 0.5,
                  paddingBlock: 14,
                  paddingInline: 16,
                  width: 140,
                }}
                onClick={handleRejectClick}
              >
                <CloseCircleOutlineIcon color={Colors.white} />
                <Text bold color={Colors.white} fontSize={18}>
                  Reject
                </Text>
              </button>
              <button
                className="shadow-xs hover__shadow-sm"
                data-cy="candidate-action-hire"
                disabled={!canHire}
                type="button"
                style={{
                  alignItems: 'center',
                  background: Colors.primary_500,
                  borderRadius: 48,
                  cursor: canHire ? 'pointer' : 'not-allowed',
                  display: 'flex',
                  gap: 6,
                  height: 48,
                  justifyContent: 'center',
                  opacity: canHire ? 1 : 0.5,
                  paddingBlock: 14,
                  paddingInline: 16,
                  width: 140,
                }}
                onClick={handleHireClick}
              >
                <CheckCircleOutlineIcon color={Colors.white} />
                <Text bold color={Colors.white} fontSize={18}>
                  Hire
                </Text>
              </button>
            </Row>
          </Row>

          <Row gap={20} marginTop={18}>
            <Column flex={1}>
              <ExperienceOverview
                hasWorkedWithDSO={currentCounter?.hasWorkedWithDSO}
                hasWorkedWithOffice={currentCounter?.hasWorkedWithOffice}
                job={currentJob}
                professional={professional}
              />
            </Column>
            <Column flex={1}>
              {data && (
                <OnsiteInterviewsPanel
                  postingId={currentJob?.postingId ?? ''}
                  groups={data.groups}
                  maps={data.maps}
                  officeId={currentJob?.offer_owner?.id}
                  officeStatus={data.officeStatus}
                  professionalId={professional.id}
                  professionalName={professional.full_name || ''}
                />
              )}
            </Column>
          </Row>
        </FloatingContainer>
      </Screen>

      {isHireOpen && !!candidate && (
        <HirePopupConfirmation
          candidateId={candidate.id}
          controls={controlsHire}
          professionalName={professional.full_name}
        />
      )}

      {isRejectOpen && !!candidate && (
        <RejectPopupConfirmation
          candidateId={candidate.id}
          controls={controlsReject}
          hasScheduledInterview={data?.hasScheduledInterview}
          professionalName={professional.full_name}
        />
      )}
    </>
  );
};
